import { doc, getDoc } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, createNamedDocument, db } from "./firestore.utils";
import {
  APP_ROLES,
  fetchInviteByEmail,
  normalizeInviteEmail,
  revokeInvite,
} from "./invite.utils";
import {
  syncScheduleForUserRole,
} from "./scheduleSync.utils";

export const AUTH_ERRORS = {
  NOT_PROVISIONED:
    "Your account hasn't been set up yet. Contact an administrator for access.",
  INACTIVE: "Your account is inactive. Contact an administrator.",
  INVALID_CREDENTIALS: "Your credentials are incorrect.",
  INVALID_ROLE:
    "Your account role is not authorized for this app. Contact an administrator.",
};

const APP_ROLES_SET = new Set(Object.values(APP_ROLES));

const isTruthyFlag = (value) =>
  value === true || value === "true" || value === 1;

export const fetchUserProfile = async (uid) => {
  const snapshot = await getDoc(doc(db, "users", uid));
  if (!snapshot.exists()) {
    return null;
  }
  return { id: snapshot.id, ...snapshot.data() };
};

export const isActiveProfile = (profile) =>
  profile?.isActive === undefined || isTruthyFlag(profile?.isActive);

export const hasValidAppRole = (profile) => APP_ROLES_SET.has(profile?.role);

export const isAdministrationProfile = (profile) =>
  isActiveProfile(profile) &&
  (profile?.role === APP_ROLES.ADMINISTRATION || isTruthyFlag(profile?.isAdmin));

export const isDispatcherOrAdministrationProfile = (profile) =>
  isActiveProfile(profile) &&
  (profile?.role === APP_ROLES.ADMINISTRATION ||
    profile?.role === APP_ROLES.DISPATCHER ||
    isTruthyFlag(profile?.isAdmin));

export const isAdminProfile = isAdministrationProfile;

export const getAuthErrorMessage = (reason) => {
  if (reason === "not_provisioned") {
    return AUTH_ERRORS.NOT_PROVISIONED;
  }
  if (reason === "inactive") {
    return AUTH_ERRORS.INACTIVE;
  }
  if (reason === "invalid_role") {
    return AUTH_ERRORS.INVALID_ROLE;
  }
  return AUTH_ERRORS.INVALID_CREDENTIALS;
};

export const signOutInactiveUser = async () => {
  await signOut(auth);
};

const buildUserFromInvite = (firebaseUser, invite) => {
  const authEmail = normalizeInviteEmail(firebaseUser.email);

  return {
    createdAt: invite.createdAt || new Date(),
    displayName: invite.displayName?.trim() || "",
    email: authEmail,
    isActive: invite.isActive,
    isAdmin: invite.role === APP_ROLES.ADMINISTRATION,
    photoStoragePath: "",
    photoUrl: firebaseUser.photoURL || "",
    role: invite.role,
    userId: firebaseUser.uid,
  };
};

export const provisionUserFromInvite = async (firebaseUser) => {
  const authEmail = normalizeInviteEmail(firebaseUser?.email);
  if (!authEmail) {
    return { ok: false, reason: "not_provisioned", profile: null };
  }

  const invite = await fetchInviteByEmail(authEmail);
  if (!invite) {
    return { ok: false, reason: "not_provisioned", profile: null };
  }

  if (!APP_ROLES_SET.has(invite.role)) {
    return { ok: false, reason: "invalid_role", profile: null };
  }

  const user = buildUserFromInvite(firebaseUser, invite);

  try {
    await createNamedDocument(doc(db, "users", firebaseUser.uid), user);
    await revokeInvite(authEmail);
    await syncScheduleForUserRole({
      role: invite.role,
      userId: firebaseUser.uid,
      email: authEmail,
      displayName: user.displayName,
      isActive: user.isActive,
    });
  } catch (error) {
    const existingProfile = await fetchUserProfile(firebaseUser.uid);
    if (existingProfile) {
      return {
        ok: true,
        reason: null,
        profile: existingProfile,
      };
    }

    throw error;
  }

  return {
    ok: true,
    reason: null,
    profile: { id: firebaseUser.uid, ...user },
  };
};

export const validateActiveUser = async (firebaseUser) => {
  if (!firebaseUser?.uid) {
    return { ok: false, reason: "not_signed_in", profile: null };
  }

  let profile = await fetchUserProfile(firebaseUser.uid);

  if (!profile) {
    const provisionResult = await provisionUserFromInvite(firebaseUser);
    if (!provisionResult.ok) {
      return {
        ok: false,
        reason: provisionResult.reason,
        profile: null,
      };
    }
    profile = provisionResult.profile;
  }

  if (!hasValidAppRole(profile)) {
    return { ok: false, reason: "invalid_role", profile };
  }

  if (!isActiveProfile(profile)) {
    return { ok: false, reason: "inactive", profile };
  }

  return { ok: true, reason: null, profile };
};
